import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ClassNames } from '@emotion/react';
import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import MEDIA_QUERY from '@zola/zola-ui/src/styles/emotion/mediaQuery';
import Button from '@zola/zola-ui/src/components/Button/ButtonV2';
import { Confetti } from '@zola/zola-ui/src/components/Confetti';
import { Mp4GifEmulator } from '@zola/zola-ui/src/components/Mp4GifEmulator';
import {
  trackCtaClicked,
  trackCtaViewed,
  CTA_TYPES,
  CtaEventDataType,
} from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';

// types
import type { RootState } from 'reducers/index';
import { getOwnerFirstName, getPartnerFirstName } from 'selectors/user';

import mp4 from 'assets/vids/post-wedding-celebration.mp4';

import {
  Container,
  ContentContainer,
  Title,
  Content,
  PTag,
  Close,
} from './PostWeddingCelebrationContent.styles';

type Action = 'continue' | 'dismiss';

type PostWeddingCelebrationContentProps = {
  setShowModal: (state: boolean) => void;
};

const PostWeddingCelebrationContent = ({
  setShowModal,
}: PostWeddingCelebrationContentProps): JSX.Element => {
  const ownerFirstName = useSelector((state: RootState) => getOwnerFirstName(state));
  const partnerFirstName = useSelector((state: RootState) => getPartnerFirstName(state));

  const getCtaEventData = (action: Action): CtaEventDataType => ({
    business_unit: 'CORE',
    group_id: 'POST_WEDDING_CELEBRATION',
    cta_type: action === 'continue' ? CTA_TYPES.BUTTON : CTA_TYPES.ICON,
    cta_id: `modal-${action}-your-wedding`,
    position: action === 'continue' ? 1 : 0,
  });

  useEffect(() => {
    const actions: Action[] = ['dismiss', 'continue'];
    actions.forEach((action) => trackCtaViewed(getCtaEventData(action)));
  }, []);

  const handleClose = useCallback(
    (action: Action) => {
      trackCtaClicked(getCtaEventData(action));
      if (window.location.pathname === '/wedding/dashboard') {
        setShowModal(false);
      } else {
        window.location.assign(`/wedding/dashboard?referrer=${window.location.pathname}`);
      }
    },
    [setShowModal]
  );

  const escFunction = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        handleClose('dismiss');
      }
    },
    [handleClose]
  );

  useEffect(() => {
    document.addEventListener('keyup', escFunction);

    return () => {
      document.removeEventListener('keyup', escFunction);
    };
  });

  return (
    <Container>
      <Close
        aria-label="Close"
        type="button"
        className="modal-close"
        onClick={() => handleClose('dismiss')}
      >
        <span aria-hidden="true">×</span>
      </Close>
      <Confetti />
      <ContentContainer>
        <ClassNames>
          {({ css }) => (
            <Mp4GifEmulator
              src={mp4}
              className={css`
                width: 200px;
                ${MEDIA_QUERY.EXCLUDE_MOBILE} {
                  width: 250px;
                }
              `}
            />
          )}
        </ClassNames>
        <Title>
          Dear {ownerFirstName} and {partnerFirstName}:
        </Title>
        <Content>
          <PTag>You did it! Cheers to you and your new adventures.</PTag>

          <PTag>
            From all of us here at Zola, we send our heartfelt thanks and congratulations. We hope
            your celebration was everything you wanted it to be, and we’re so honored to have been a
            part of it!
          </PTag>

          <PTag>
            We know this time can be magical, but also a bit stressful. Next up, you’ll find
            everything you need to make the transition to newlywed life a breeze—gift management,
            thank you cards, photo albums, and more. Forever and always, we’ve got your back.
          </PTag>

          <PTag>
            Love,
            <br />
            Team Zola
          </PTag>
        </Content>
        <ClassNames>
          {({ css }) => (
            <Button
              fullWidth={isMobile()}
              onClick={() => handleClose('continue')}
              className={css`
                margin-left: auto !important;
                margin-bottom: 50px !important;
                flex: 0 0 auto;
              `}
            >
              Continue Your Journey
            </Button>
          )}
        </ClassNames>
      </ContentContainer>
    </Container>
  );
};

export default PostWeddingCelebrationContent;
